import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Panel,
  NodeTypes,
  EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Workflow, Group, WorkflowCustomField } from '../types';
import { API_URL } from '../config';
import axios from 'axios';
import BPMNNode from './BPMNNode';
import BPMNEdge from './BPMNEdge';
import WorkflowStepDetails from './WorkflowStepDetails';
import './WorkflowBPMNViewer.css';
import dagre from 'dagre';

interface WorkflowBPMNViewerProps {
  workflow: Workflow | null;
  currentStep?: string;
}

interface StepData extends Record<string, unknown> {
  step: any;
  groups?: Group[];
  customFields?: WorkflowCustomField[];
  stepConfigs?: any[];
}

const nodeTypes: NodeTypes = {
  startEvent: BPMNNode,
  endEvent: BPMNNode,
  activity: BPMNNode,
  gateway: BPMNNode,
};

const edgeTypes: EdgeTypes = {
  default: BPMNEdge,
  alternative: BPMNEdge,
};

const WorkflowBPMNViewer: React.FC<WorkflowBPMNViewerProps> = ({ workflow, currentStep }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<StepData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [workflowGroups, setWorkflowGroups] = useState<Group[]>([]);
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [stepConfigs, setStepConfigs] = useState<any[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(undefined);
  
  const clearSelectedNode = useCallback(() => {
    setSelectedNode(undefined);
  }, []);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  // Buscar dados adicionais do workflow
  useEffect(() => {
    if (!workflow) {
      setLoading(false);
      return;
    }

    const fetchAdditionalData = async () => {
      try {
        const [groupsResponse, fieldsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/workflows/${workflow.id}/groups`),
          axios.get(`${API_URL}/api/workflows/${workflow.id}/custom-fields`)
        ]);

        const groups = groupsResponse.data.map((wg: any) => {
          if (wg.group) {
            return {
              id: wg.group.id,
              name: wg.group.name,
              description: wg.group.description || '',
              is_active: wg.group.is_active
            };
          }
          return {
            id: wg.group_id,
            name: '',
            description: '',
            is_active: true
          };
        });
        setWorkflowGroups(groups);

        const fields = fieldsResponse.data;
        setCustomFields(fields);

        // Buscar configurações de step para cada campo
        const configsPromises = fields.map(async (field: any) => {
          try {
            const configResponse = await axios.get(
              `${API_URL}/api/workflows/${workflow.id}/custom-fields/${field.id}/step-configs`
            );
            return configResponse.data.map((config: any) => ({
              ...config,
              fieldId: field.id,
              fieldName: field.name,
              fieldLabel: field.label
            }));
          } catch {
            return [];
          }
        });

        const allConfigs = (await Promise.all(configsPromises)).flat();
        setStepConfigs(allConfigs);
      } catch (error) {
        console.error('Erro ao carregar dados adicionais:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdditionalData();
  }, [workflow]);

  // Converter steps para nós e arestas BPMN
  const { bpmnNodes, bpmnEdges } = useMemo(() => {
    if (!workflow || !workflow.steps || loading) {
      return { bpmnNodes: [], bpmnEdges: [] };
    }

    let steps: any[] = [];
    try {
      const stepsData = JSON.parse(workflow.steps);
      if (Array.isArray(stepsData)) {
        steps = stepsData;
      } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
        steps = stepsData.steps;
      }
    } catch (e) {
      steps = workflow.steps.split(',').map((s: string) => ({ name: s.trim(), status: 'em_andamento' }));
    }

    if (steps.length === 0) {
      return { bpmnNodes: [], bpmnEdges: [] };
    }

    // Normalizar steps
    const normalizedSteps = steps.map((step, idx) => {
      const stepName = typeof step === 'string' ? step : step.name || step;
      const stepObj = typeof step === 'object' ? step : { name: step, status: 'em_andamento' };
      return {
        id: idx,
        name: stepName,
        status: stepObj.status || 'em_andamento',
        description: stepObj.description,
        previous_steps: stepObj.previous_steps || []
      };
    });

    // Criar grafo para layout automático
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'LR', nodesep: 100, ranksep: 150 });

    const nodeMap = new Map<string, any>();
    const nodesList: Node<StepData>[] = [];
    const edgesList: Edge[] = [];

    // Evento de Início
    const startNodeId = 'start-event';
    g.setNode(startNodeId, { width: 60, height: 60 });
    nodesList.push({
      id: startNodeId,
      type: 'startEvent',
      position: { x: 0, y: 0 },
      data: { 
        label: 'Início',
        step: { name: 'Início', status: 'inicio' },
        isStart: true
      },
      style: { width: 60, height: 60 }
    });
    nodeMap.set('start', startNodeId);

    // Criar nós para cada step
    normalizedSteps.forEach((step, index) => {
      const nodeId = `step-${index}`;
      const hasMultiplePredecessors = step.previous_steps && step.previous_steps.length > 0;
      const nodeType = hasMultiplePredecessors ? 'gateway' : 'activity';
      
      // Obter grupos e campos customizados para este step
      const stepCustomFields = customFields.filter(field => {
        const config = stepConfigs.find(c => c.step_name === step.name && c.fieldId === field.id);
        return config && config.is_visible;
      });

      g.setNode(nodeId, { width: 180, height: 100 });
      
      const isCurrent = currentStep === step.name;
      const currentStepIndex = currentStep ? normalizedSteps.findIndex(s => s.name === currentStep) : -1;
      const isCompleted = currentStepIndex > index;
      const isPending = !isCurrent && !isCompleted;

      nodesList.push({
        id: nodeId,
        type: nodeType,
        position: { x: 0, y: 0 }, // Será calculado pelo dagre
        data: {
          label: step.name,
          step: step,
          groups: workflowGroups,
          customFields: stepCustomFields,
          stepConfigs: stepConfigs.filter(c => c.step_name === step.name),
          status: step.status,
          isCurrent,
          isCompleted,
          isPending,
          description: step.description
        },
        style: { width: 180, height: 100 }
      });
      nodeMap.set(step.name, nodeId);
    });

    // Evento de Fim
    const endNodeId = 'end-event';
    g.setNode(endNodeId, { width: 60, height: 60 });
    nodesList.push({
      id: endNodeId,
      type: 'endEvent',
      position: { x: 0, y: 0 },
      data: { 
        label: 'Fim',
        step: { name: 'Fim', status: 'fim' },
        isEnd: true
      },
      style: { width: 60, height: 60 }
    });
    nodeMap.set('end', endNodeId);

    // Criar arestas
    // Conexão do início para o primeiro step
    if (normalizedSteps.length > 0) {
      const firstStepId = `step-0`;
      g.setEdge(startNodeId, firstStepId);
      edgesList.push({
        id: `edge-start-${firstStepId}`,
        source: startNodeId,
        target: firstStepId,
        type: 'default',
        animated: false,
        style: { stroke: '#6c757d', strokeWidth: 2 }
      });
    }

    const edgeKeys = new Set<string>();
    const addEdge = (edge: Edge) => {
      const key = `${edge.source}->${edge.target}`;
      if (edgeKeys.has(key)) return;
      edgeKeys.add(key);
      edgesList.push(edge);
    };

    // Conexões sequenciais e alternativas
    normalizedSteps.forEach((step, index) => {
      const stepId = `step-${index}`;
      const hasPreviousSteps = step.previous_steps && step.previous_steps.length > 0;

      // Conexão sequencial: sempre que o próximo passo não declara predecessores explícitos
      if (index < normalizedSteps.length - 1) {
        const nextStepId = `step-${index + 1}`;
        const nextStep = normalizedSteps[index + 1];
        const nextHasPreviousSteps = nextStep.previous_steps && nextStep.previous_steps.length > 0;

        if (!nextHasPreviousSteps) {
          g.setEdge(stepId, nextStepId);
          addEdge({
          id: `edge-${stepId}-${nextStepId}`,
          source: stepId,
          target: nextStepId,
          type: 'default',
          animated: false,
          style: { stroke: '#6c757d', strokeWidth: 2 }
          });
        }
      }

      if (hasPreviousSteps) {
        // Conexões alternativas de predecessores explícitos para o passo atual
        step.previous_steps.forEach((prevStepName: string) => {
          const prevStepIndex = normalizedSteps.findIndex(s => s.name === prevStepName);
          if (prevStepIndex >= 0) {
            const prevStepId = `step-${prevStepIndex}`;
            g.setEdge(prevStepId, stepId);
            addEdge({
              id: `edge-${prevStepId}-${stepId}-alt`,
              source: prevStepId,
              target: stepId,
              type: 'alternative',
              animated: true,
              style: { stroke: '#0dcaf0', strokeWidth: 3, strokeDasharray: '5,5' }
            });
          }
        });
      }
    });

    // Conexão do último step para o fim
    // Encontrar steps que não têm sucessores (últimos steps do fluxo)
    const stepsWithSuccessors = new Set<string>();
    normalizedSteps.forEach((step) => {
      if (step.previous_steps && step.previous_steps.length > 0) {
        step.previous_steps.forEach((prev: string) => stepsWithSuccessors.add(prev));
      }
    });
    
    // Encontrar steps que são últimos (não têm sucessores ou são o último sequencial)
    const lastSteps = normalizedSteps.filter((step, index) => {
      const isLastSequential = index === normalizedSteps.length - 1;
      const hasNoSuccessor = !stepsWithSuccessors.has(step.name);
      return isLastSequential || hasNoSuccessor;
    });

    // Conectar todos os últimos steps ao evento de fim
    lastSteps.forEach((lastStep) => {
      const lastStepIndex = normalizedSteps.findIndex(s => s.name === lastStep.name);
      if (lastStepIndex >= 0) {
        const lastStepId = `step-${lastStepIndex}`;
        g.setEdge(lastStepId, endNodeId);
        edgesList.push({
          id: `edge-${lastStepId}-end`,
          source: lastStepId,
          target: endNodeId,
          type: 'default',
          animated: false,
          style: { stroke: '#6c757d', strokeWidth: 2 }
        });
      }
    });

    // Calcular layout com dagre
    dagre.layout(g);

    // Atualizar posições dos nós
    const positionedNodes = nodesList.map(node => {
      const nodeWithPosition = g.node(node.id);
      if (nodeWithPosition) {
        const width = typeof node.style?.width === 'number' ? node.style.width : (typeof node.style?.width === 'string' ? parseInt(node.style.width) || 180 : 180);
        const height = typeof node.style?.height === 'number' ? node.style.height : (typeof node.style?.height === 'string' ? parseInt(node.style.height) || 100 : 100);
        return {
          ...node,
          position: {
            x: nodeWithPosition.x - width / 2,
            y: nodeWithPosition.y - height / 2
          }
        };
      }
      return node;
    });

    return { bpmnNodes: positionedNodes, bpmnEdges: edgesList };
  }, [workflow, currentStep, workflowGroups, customFields, stepConfigs, loading]);

  // Atualizar nós e arestas quando mudarem
  useEffect(() => {
    setNodes(bpmnNodes);
    setEdges(bpmnEdges);
  }, [bpmnNodes, bpmnEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node<StepData>) => {
    setSelectedNode(node);
    setShowDetails(true);
  }, []);

  const onPaneClick = useCallback(() => {
    clearSelectedNode();
    setShowDetails(false);
  }, []);

  if (!workflow || !workflow.steps) {
    return (
      <div className="bpmn-viewer-empty">
        <p className="text-muted">Nenhum fluxo configurado</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bpmn-viewer-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
        <p className="mt-2">Carregando diagrama...</p>
      </div>
    );
  }

  return (
    <div className="bpmn-viewer-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      >
        <Background color="#e5e7eb" gap={16} />
        <Controls />
        <MiniMap 
          nodeColor={(node: Node<StepData>) => {
            if (node.data?.isCurrent) return '#1d428a';
            if (node.data?.isCompleted) return '#198754';
            return '#6c757d';
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
        <Panel position="top-left" className="bpmn-legend">
          <div className="bpmn-legend-content">
            <div className="legend-item">
              <div className="legend-icon start"></div>
              <span>Início</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon activity"></div>
              <span>Atividade</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon gateway"></div>
              <span>Gateway</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon end"></div>
              <span>Fim</span>
            </div>
            <div className="legend-item">
              <div className="legend-line sequential"></div>
              <span>Sequencial</span>
            </div>
            <div className="legend-item">
              <div className="legend-line alternative"></div>
              <span>Alternativo</span>
            </div>
          </div>
        </Panel>
      </ReactFlow>

      {selectedNode && (
        <WorkflowStepDetails
          show={showDetails}
          onHide={() => {
            setShowDetails(false);
            clearSelectedNode();
          }}
          node={selectedNode}
        />
      )}
    </div>
  );
};

export default WorkflowBPMNViewer;

